import React from "react";
import { cn } from "../../../common/u1/cn";

/* The two shapes every profile section is built from. */
export const ProfileField = ({ label, children, className = "" }) => {
  /* A label with nothing under it is worse than no row: it reads as data that
     failed to load. Whitespace-only strings count as nothing — the API returns
     " " for some cleared fields, which is truthy. */
  const empty =
    children == null ||
    children === false ||
    (typeof children === "string" && children.trim() === "");
  if (empty) return null;

  return (
    <div className={cn("min-w-0", className)}>
      <p className="m-0 text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans">
        {label}
      </p>
      <p className="mt-1 mb-0 break-words text-[14px] text-[var(--ink)] font-sans">
        {children}
      </p>
    </div>
  );
};

export const ProfileCard = ({ title, action, children, className = "", ...rest }) => (
  <section
    className={cn(
      "rounded-[14px] border border-[var(--line)] bg-white p-5 sm:p-6",
      className
    )}
    {...rest}
  >
    {(title || action) && (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {title && (
          <h3 className="m-0 text-[17px] font-bold text-[var(--ink)] font-sans">{title}</h3>
        )}
        {action}
      </div>
    )}
    {children}
  </section>
);

/* A section's table. The five profile tables share their head treatment and
   their horizontal scroller, so they share this. */
export const ProfileTable = ({ columns = [], children, minWidth = "640px" }) => (
  <div className="-mx-1 overflow-x-auto px-1">
    <table className="w-full border-collapse" style={{ minWidth }}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={typeof col === "string" ? col : col.label}
              scope="col"
              className={cn(
                "border-b border-[var(--line)] px-2 pb-2.5 text-left",
                "text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans",
                typeof col === "object" && col.align === "right" && "text-right"
              )}
            >
              {typeof col === "string" ? col : col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const TD = ({ children, className = "", ...rest }) => (
  <td
    className={cn(
      "border-b border-[var(--line)] px-2 py-3.5 align-middle",
      "text-[13.5px] text-[var(--ink-2)] font-sans",
      className
    )}
    {...rest}
  >
    {children}
  </td>
);

/* A small tinted pill — a relation type, a stakeholder kind, a nationality. */
const PILL_TONES = {
  neutral: "bg-[var(--slate-2)] text-[var(--ink-2)]",
  coral: "bg-[var(--coral-soft)] text-[var(--coral-deep)]",
  navy: "bg-[var(--claimed-bg)] text-[var(--navy)]",
  green: "bg-[var(--success-soft)] text-[var(--success)]",
  gold: "bg-[var(--gold-soft)] text-[var(--gold-deep)]",
  violet: "bg-[var(--pill-violet-bg)] text-[var(--pill-violet-ink)]",
  teal: "bg-[var(--pill-teal-bg)] text-[var(--pill-teal-ink)]",
};

export const Pill = ({ tone = "neutral", children, className = "" }) => (
  <span
    className={cn(
      "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1",
      "text-[11.5px] font-semibold font-sans",
      PILL_TONES[tone] || PILL_TONES.neutral,
      className
    )}
  >
    {children}
  </span>
);

/* Tab 01's three cards. They live here rather than in GeneralInformation.jsx because two callers need them: the tab itself, and GatedTabPreview, which renders... */
export const IndustryActivities = ({ activities = [] }) => {
  if (activities.length === 0) return null;

  return (
    <ProfileCard title="Industry / Activities">
      <ul className="m-0 flex list-none flex-wrap gap-2.5 p-0">
        {activities.map((activity) => (
          <li
            key={activity.id ?? activity.label}
            className={cn(
              "inline-flex items-center gap-2 rounded-full bg-[#f5f1e8] px-3.5 py-2",
              "text-[13px] font-medium text-[var(--ink)] font-sans"
            )}
          >
            <span aria-hidden="true" className="h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--coral)]" />
            {activity.label}
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
};

export const AddressCard = ({ address = {} }) => {
  /* Nothing to show is no card, rather than a heading over blank space. */
  const hasAny = Object.values(address).some(Boolean);
  if (!hasAny) return null;

  return (
    <ProfileCard title="Address">
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        <ProfileField label="State">{address.state}</ProfileField>
        <ProfileField label="City">{address.city}</ProfileField>
        <ProfileField label="Area">{address.area}</ProfileField>
        <ProfileField label="ZIP Code">{address.zip}</ProfileField>
        <ProfileField label="Address Line 1" className="sm:col-span-2">
          {address.addressLine1}
        </ProfileField>
        <ProfileField label="Email Address">{address.email}</ProfileField>
        <ProfileField label="Telephone">{address.telephone}</ProfileField>
        <ProfileField label="Mobile Number">{address.phone}</ProfileField>
        <ProfileField label="Working Hours">{address.workingHours}</ProfileField>
      </div>
    </ProfileCard>
  );
};

export const AboutCompany = ({ description }) => {
  if (!description) return null;

  return (
    <ProfileCard title="About Company">
      {/* Stored as HTML and rendered as HTML, exactly as the live tab does. */}
      <div
        className="text-[14px] leading-[1.65] text-[var(--ink-2)] font-sans [&_a]:text-[var(--coral)] [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </ProfileCard>
  );
};

export default ProfileCard;
